import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { CalendarDays, DollarSign, Banknote, CreditCard } from 'lucide-react';
import apiClient from '@/services/api';
import type { SalesSummaryReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** End-of-Day Summary: per-day takings with cash/card/other split. */
export default function EndOfDayReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<SalesSummaryReport>({
    queryKey: ['sales-summary', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getSalesSummary(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const days = data?.byDay ?? [];
  const t = data?.totals;
  const cash = days.reduce((a, d) => a + Number(d.cash), 0);
  const card = days.reduce((a, d) => a + Number(d.card), 0);
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });
  const fmtDay = (s: string) => format(parseISO(s), 'EEE MMM dd');

  const sections = (): ReportSection[] => [
    {
      heading: 'Daily Summary',
      headers: ['Date', 'Orders', 'Net', 'Tax', 'Discount', 'Service', 'Delivery', 'Tip', 'Cash', 'Card', 'Other', 'Total'],
      rows: days.map((d) => [fmtDay(d.date), d.orders, formatCurrency(d.subtotal), formatCurrency(d.tax), formatCurrency(d.discount), formatCurrency(d.serviceCharge), formatCurrency(d.deliveryCharge), formatCurrency(d.tip), formatCurrency(d.cash), formatCurrency(d.card), formatCurrency(d.other), formatCurrency(d.total)]),
    },
  ];
  const kpis = (): [string, string][] => [
    ['Days', days.length.toLocaleString()],
    ['Total', formatCurrency(t?.total ?? 0)],
    ['Cash', formatCurrency(cash)],
    ['Card', formatCurrency(card)],
  ];

  return (
    <ReportFrame
      title="End-of-Day Summary"
      description="Per-day takings with cash / card / other tender split."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={days.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`end-of-day-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('End-of-Day Summary', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('End-of-Day Summary', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Days" value={days.length.toLocaleString()} icon={CalendarDays} palette="blue" />
        <KPICard label="Total" value={formatCurrency(t?.total ?? 0)} icon={DollarSign} palette="green" />
        <KPICard label="Cash" value={formatCurrency(cash)} icon={Banknote} palette="purple" />
        <KPICard label="Card" value={formatCurrency(card)} icon={CreditCard} palette="red" />
      </div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">Daily Summary</h3>
        <SimpleTable
          headers={['Date', 'Orders', 'Net', 'Tax', 'Disc.', 'Service', 'Delivery', 'Tip', 'Cash', 'Card', 'Other', 'Total']}
          rows={days.map((d) => [fmtDay(d.date), d.orders, formatCurrency(d.subtotal), formatCurrency(d.tax), formatCurrency(d.discount), formatCurrency(d.serviceCharge), formatCurrency(d.deliveryCharge), formatCurrency(d.tip), formatCurrency(d.cash), formatCurrency(d.card), formatCurrency(d.other), formatCurrency(d.total)])}
        />
      </div></div>
    </ReportFrame>
  );
}
